<template>
  <div class="aivideo-player">
    <div
      ref="aiVideoPlayer"
      class="aivideo-player-box"
      :class="[{ 'is-fullscreen': isFullscreen }]"
    >
      <div id="aiVideoPlayer"></div>

      <div class="player-pause" v-if="!isPlaying">
        <p>
          <i class="vh-iconfont vh-line-video-play" @click="handlePlayToggle"></i>
        </p>
      </div>
      <!-- 自定义视频控制栏 -->
      <transition name="fade">
        <div class="player-controls" v-if="isPlaying">
          <!-- 进度条 -->
          <div class="player-controls__progressbar">
            <vh-slider
              ref="controllerRef"
              class="player-controls-progress"
              v-model="progress"
              :show-tooltip="false"
              :step="1 / Math.floor(totalTime)"
              @change="handleChangeProgress(progress, true)"
              @mousedown.native="handleSliderMouseDown"
            ></vh-slider>
            <!-- 视频打点 -->
            <div class="player-controls__markpoints">
              <vh-tooltip
                class="item"
                effect="light"
                :content="item.subject"
                placement="top"
                popper-class="player-markpoint-popper"
                v-for="item in markpoints"
                :key="`mp_${item.start_time}`"
              >
                <div
                  class="player-markpoint"
                  :style="{ left: getPointLeft(item.time) + '%' }"
                  @click.stop.prevent="handleClickMarkpoint(item.time)"
                ></div>
              </vh-tooltip>
            </div>
          </div>

          <!-- 视频操作 -->
          <div class="player-controls__op">
            <!-- 播放/暂停 -->
            <div class="player-controls-play" @click="handlePlayToggle">
              <i
                :class="`vh-iconfont ${isPlaying ? 'vh-a-line-videopause' : 'vh-line-video-play'}`"
              ></i>
            </div>
            <!-- 时间 -->
            <div class="player-controls-time">
              <template v-if="totalTime > 0">
                <span class="player-controls-time__current">
                  {{ currentTime | secondToTime }}
                </span>
                <span class="player-controls-time__divider">/</span>
                <span class="player-controls-time__total">{{ totalTime | secondToTime }}</span>
              </template>
              <template v-else>
                <span>--:--/--:--</span>
              </template>
            </div>

            <!-- 倍速 -->
            <div
              class="player-controls-speed"
              @mouseenter="handleMouseEnter"
              @mouseleave="handleMouseLeave"
            >
              <transition name="fade-in-linear">
                <ul
                  v-show="showPopper"
                  class="player-controls-speed__options"
                  @mouseenter="handleMouseEnter"
                  @mouseleave="handleMouseLeave"
                >
                  <li
                    v-for="item in speedOptions"
                    :key="`speed${item}`"
                    :class="[{ selected: speed == item }]"
                    @click="handleSpeed(item)"
                  >
                    {{ item }}X
                  </li>
                </ul>
              </transition>
              <span>{{ $t('ai.k_007001') }}</span>
            </div>

            <!-- 音量 -->
            <div class="player-controls-volume">
              <i
                class="vh-iconfont"
                :class="`${voice > 0 ? 'vh-line-voice' : 'vh-line-mute'}`"
                @click="handleMute"
              ></i>
              <vh-slider
                class="player-controls-volume__slider"
                @change="handleVoiceChange"
                v-model="voice"
                vertical
                height="100px"
                :show-tooltip="true"
              ></vh-slider>
            </div>

            <!-- 画中画 -->
            <div class="player-controls-pip">
              <i
                class="vh-iconfont"
                :class="`${isPip ? 'vh-line-tuichuhuazhonghua' : 'vh-line-huazhonghua'}`"
                @click="handleTogglePip"
              ></i>
            </div>

            <!-- 全屏 -->
            <div class="player-controls-fullscreen">
              <i
                class="vh-iconfont"
                :class="`${isFullscreen ? 'vh-a-line-exitfullscreen' : 'vh-a-line-fullscreen'}`"
                @click="enterFullscreen"
              ></i>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>
<script>
  import { sessionOrLocal, base64ToFile } from '@/tools/utils';
  import dayjs from 'dayjs';
  import screenfull from 'screenfull';
  import { getPlayBackInfo } from '@/biz/record.js';

  export default {
    name: 'AiVideoPlayer',
    data() {
      return {
        docSDK: null,
        aiPlayer: null,
        loaded: false, //视频是否加载完成
        subtitleLoaded: false, // 字幕是否挂载完成

        // 自定义视频Control相关
        speedTimer: 0,
        showPopper: false,
        speed: 1, // 当前倍速
        speedOptions: [1], //可选倍速

        voice: 0, // 音量0~100
        lastVol: 60, //记录静音之前的声音,

        currentTime: 0, // 视频当前时间
        totalTime: 0, // 视频总时长

        isPip: false, //是否画中画播放

        progress: 0, //进度条值
        isSettingProgress: false, //是否在设置进度(拖动、点击进度条)

        isFullscreen: false // 是否全屏
      };
    },
    computed: {
      isPlaying() {
        return this.$store.state.video.isPlaying || false;
      },
      // 视频打点
      markpoints() {
        return this.$store.state.video.markpoints || [];
      },
      userId() {
        return this.$store.state.userInfo?.user_id;
      },
      revoId() {
        return this.$isEmbed
          ? this.$store.state.video.recordInfo?.vod_id
          : this.$store.state.video.recordInfo?.id;
      }
    },
    watch: {
      $route: {
        handler() {
          this.closeVodSubtitle();
        },
        immediate: true
      },
      revoId(newVal) {
        if (newVal) {
          this.initPlayBackInfo();
        } else {
          this.destroyPlayer();
        }
      }
    },
    methods: {
      closeVodSubtitle() {
        if (this.subtitleLoaded && !this.$route.path.includes('/video/subtitles')) {
          this.aiPlayer.closeVodSubtitle();
        }
      },
      // 获取回放详情信息
      async initPlayBackInfo() {
        // 获取回放详情信息
        await getPlayBackInfo();

        if (this.$store.state.video.playBackInfo) {
          // 初始化播放器sdk
          this.initPlayerSDK(this.$store.state.video.playBackInfo);

          if (!this.$isEmbed) {
            // 通知文档sdk初始化
            this.$emitter.emit('initDocSDK', this.$store.state.video.playBackInfo);
          }
        }
      },
      initPlayerSDK(options) {
        this.$log.info('player options:', options);
        const params = {
          appId: options.paasAppId, // 应用ID，必填
          accountId: options.accountId || this.userId, // 第三方用户ID，必填
          token: options.paasAccessToken, // access_token，必填
          videoNode: 'aiVideoPlayer',
          type: 'vod', // live 直播  vod 点播  必填
          vodOption: {
            recordId: options.player.paasRecordId,
            forceMSE: false
          },
          vodSubtitleOption: {
            enable: true,
            style: {
              /* 设置字体颜色 */
              color: '#fff',
              textDecoration: 'none',
              // 背景色由浏览器控制，自写css覆盖在chrome、safari不生效，firefox生效
              backgroundColor: 'transparent',
              fontSize: '1em',
              /* 字幕文本的“描边”效果 */
              textShadow:
                '-1px -1px 0 #000000, 1px -1px 0 #000000, -1px 1px 0 #000000, 1px 1px 0 #000000'
              // textShadow:
              //   '-1px -1px 0 rgba(0,0,0,0.6), 1px -1px 0 rgba(0,0,0,0.6), -1px 1px 0 rgba(0,0,0,0.6), 1px 1px 0 rgba(0,0,0,0.6)'
            }
          },
          // 上报数据不参与统计
          otherOption: {
            vid: 0, // hostId
            vfid: 0,
            guid: 0,
            biz_id: 0,
            report_extra: "{'join_id':0,'switch_id':0,'is_filter':1}"
          }
        };
        this.$log.info('player 初始化 params:', params);
        return new Promise((resolve, reject) => {
          window.VhallPlayer.createInstance(
            params,
            event => {
              this.$log.info('player 初始化播放器成功');
              this.aiPlayer = event.vhallplayer;

              // 关闭player sdk的控制栏
              this.aiPlayer.openControls(false);
              this.aiPlayer.openUI(false);

              // 初始时,默认静音播放
              this.aiPlayer.setVolume(0);

              this.initPlayerEvent();

              resolve(event);
            },
            e => {
              reject(e);
            }
          );
        });
      },
      initPlayerEvent() {
        // 视频加载完成
        this.aiPlayer.on(VhallPlayer.LOADED, e => {
          this.$log.info('playerevent 视频加载完成 LOADED');
          this.loaded = true;
          // this.$emit('loadeddata_ext', e);
          this.getTotalTime(); // 获取视频总时长

          this.initVideoSpeed(); // 初始化视频倍速
        });

        // 播放时间改变时触发
        this.aiPlayer.on(VhallPlayer.TIMEUPDATE, e => {
          if (this.isSettingProgress) return;
          this.currentTime = this.aiPlayer.getCurrentTime(() => {
            this.$log.error('playerevent 获取当前视频播放时间失败-----');
          });
          this.progress = (this.currentTime / this.totalTime) * 100;

          // 对外抛出事件
          this.$emitter.emit(this.$EMIT_DEFINE.videoTimeUpdate, this.currentTime);
        });

        //自动播放失败
        this.aiPlayer.on(VhallPlayer.AUTOPLAY_FAILED, e => {
          this.$log.error('playerevent 自动播放失败 AUTOPLAY_FAILED', e);
          // this.$emit('autoplayFailed', e);
        });

        // 开始播放时触发
        this.aiPlayer.on(VhallPlayer.PLAY, e => {
          // this.$emit('play_ext', e);
        });
        // 视频正在播放中
        this.aiPlayer.on(VhallPlayer.PLAYING, e => {
          this.$store.commit('video/setIsPlaying', true);
        });
        // 暂停时触发
        this.aiPlayer.on(VhallPlayer.PAUSE, e => {
          this.$store.commit('video/setIsPlaying', false);
        });

        // 播放器出错
        this.aiPlayer.on(VhallPlayer.ERROR, e => {
          // this.$emit('error', e);
        });
        // 视频播放完毕
        this.aiPlayer.on(VhallPlayer.ENDED, e => {
          // this.$emit('ended', e);
        });
        // 字幕挂载完
        this.aiPlayer.on('vodSubtitleMounted', info => {
          this.subtitleLoaded = true;
          setTimeout(() => {
            this.closeVodSubtitle();
          }, 20);
        });

        screenfull.on('error', event => {});

        screenfull.on('change', ev => {
          this.isFullscreen = !this.isFullscreen;
        });

        this.aiPlayer.on(VhallPlayer.ENTER_PICTURE_IN_PICTURE, e => {
          this.isPip = true;
        });
        this.aiPlayer.on(VhallPlayer.EXIT_PICTURE_IN_PICTURE, e => {
          this.isPip = false;
        });

        this.$emitter.on('doVideoAction', (action, params = {}) => {
          switch (action) {
            case 'pause': {
              this.aiPlayer.pause();
              break;
            }
            case 'seek': {
              if (Object.prototype.toString.call(params) === '[object Object]') {
                if (params.pause) {
                  // seek到指定时间的同时，暂停
                  this.aiPlayer.pause();
                }
                // 默认使用毫秒, 传second参数可指定秒
                const t = params.second ? params.time : params.time / 1000.0;
                this.aiPlayer?.setCurrentTime(t);
              }
              break;
            }
            // 打开点播字幕
            case 'openVodSubtitle': {
              this.aiPlayer.openVodSubtitle();
              break;
            }
            // 关闭点播字幕
            case 'closeVodSubtitle': {
              this.aiPlayer.closeVodSubtitle();
              break;
            }
          }
        });

        this.$emitter.on(this.$EMIT_DEFINE.videoAsrTextUpdate, this.asrTextUpdate);

        // 字幕内容变更监听
        this.$emitter.on(this.$EMIT_DEFINE.videoSubtitlesUpdate, this.subtitlesUpdate);

        // 字幕样式变更监听
        this.$emitter.on(this.$EMIT_DEFINE.videoSubtitlesCssUpdate, this.subtitlesCssUpdate);
      },
      // 获取回放总时长
      getTotalTime() {
        let getRecordTotalTimer = null;
        // 回放时间异步获取 需要通过定时器获取
        getRecordTotalTimer = setInterval(() => {
          try {
            this.totalTime = (this.aiPlayer && this.aiPlayer.getDuration(() => {})) || 0;
            this.totalTime > 0 && clearInterval(getRecordTotalTimer);

            this.$store.commit('video/setTotalTime', this.totalTime);
          } catch (error) {}
        }, 50);
      },
      handleMouseEnter() {
        clearTimeout(this.speedTimer);
        this.speedTimer = setTimeout(() => {
          this.showPopper = true;
        }, 50);
      },
      handleMouseLeave() {
        clearTimeout(this.speedTimer);
        this.speedTimer = setTimeout(() => {
          this.showPopper = false;
        }, 200);
        // this.hideControl();
      },
      // 设置播放时间
      setVideoCurrentTime(val) {
        if (!this.aiPlayer) return;

        this.progress = (val / this.totalTime) * 100;
        this.currentTime = val;
        this.aiPlayer.setCurrentTime(val, () => {
          console.error('设置当前播放时间失败');
        });
        this.isSettingProgress = false;
      },
      handleChangeProgress(val, isPercent) {
        this.isSettingProgress = true;
        if (isPercent) {
          const time = (val / 100) * this.totalTime; // 快进
          this.setVideoCurrentTime(time);
        } else {
          this.setVideoCurrentTime(val);
        }
      },
      handlePlayToggle() {
        if (!this.aiPlayer) return;
        if (this.isPlaying) {
          this.aiPlayer.pause();
        } else {
          this.aiPlayer.play();
        }
      },
      initVideoSpeed() {
        if (!this.aiPlayer) return;
        let usableSpeed = this.aiPlayer.getUsableSpeed(() => {});
        if (usableSpeed) {
          usableSpeed = usableSpeed.filter(value => {
            return [2, 1.75, 1.5, 1.25, 1, 0.75].includes(value);
          });

          this.speedOptions = usableSpeed;
        }
        if (sessionOrLocal.get('aiVideoLocalSpeedValue')) {
          this.speed = parseFloat(sessionOrLocal.get('aiVideoLocalSpeedValue'));
          let suc = true;
          this.aiPlayer.setPlaySpeed(this.speed, () => (suc = false));
          if (suc) {
          } else {
            this.speed = 1;
          }
        } else {
          this.speed = 1;
        }
      },
      // 倍速
      handleSpeed(val) {
        if (this.aiPlayer) {
          let suc = true;
          this.aiPlayer.setPlaySpeed(val, () => (suc = false));
          if (suc) {
            this.speed = val;
            sessionOrLocal.set('aiVideoLocalSpeedValue', val);
          } else {
          }
        }
      },
      // 声音值改变时触发（使用鼠标拖曳时，只在松开鼠标后触发）
      handleVoiceChange(newVal) {
        this.aiPlayer.setVolume(newVal);
      },
      // 点击设置为静音/还原
      handleMute() {
        if (this.voice == 0) {
          this.voice = this.lastVol;
        } else {
          this.lastVol = this.voice;
          this.voice = 0;
        }
        this.aiPlayer.setVolume(this.voice);
      },
      // 画中画
      handleTogglePip() {
        if (!this.aiPlayer) return;
        if (this.isPip) {
          /* prettier-ignore */
          this.aiPlayer.exitPictureInPicture().then(() => {
          this.isPip = false;
        }).catch((err) => {
          console.warn(err);
        });
        } else {
          /* prettier-ignore */
          this.aiPlayer.requestPictureInPicture().then(() => {
          this.isPip = true;
        }).catch((err) => {
          console.warn(err);
        });
        }
      },
      // 全屏/退出餍
      enterFullscreen() {
        screenfull.toggle(this.$refs.aiVideoPlayer);
      },
      handleSliderMouseDown() {
        // 鼠标拖动的时候，阻止slider值更新
        if (!this.isSettingProgress) {
          this.isSettingProgress = true;
        }
      },
      getPointLeft(time) {
        return ((time / this.totalTime) * 100).toFixed(3) - 0.2;
      },
      handleClickMarkpoint(val) {
        this.handleChangeProgress(val);
      },
      // 智能解析内容更新后触发
      asrTextUpdate() {
        if (this.aiPlayer && this.loaded) {
          // 重新挂载字幕，确保切换到智能字幕页面后能正确显示
          this.aiPlayer.reloadVodSubtitle();
        }
      },
      // 智能字幕内容更新后触发
      subtitlesUpdate() {
        if (this.aiPlayer && this.loaded) {
          // 重新挂载字幕，确保切换到智能字幕页面后能正确显示
          this.aiPlayer.reloadVodSubtitle();
        }
      },
      // 智能字幕样式设置变更触发
      subtitlesCssUpdate(cssObj) {
        if (!this.aiPlayer || !cssObj) return;
        // {
        //     "font_size": 1,
        //     "font_color": "#ffffff",
        //     "is_bold": 1,
        //     "is_underline": 0,
        //     "is_incline": 0,
        //     "opacity": 100,
        //     "stroke_color": "#000000",
        //     "stroke_width": 1
        // }
        const transCssObj = {
          fontSize: `${cssObj.font_size}em`,
          color: cssObj.font_color,
          fontWeight: cssObj.is_bold ? 'bold' : 'normal',
          textDecoration: cssObj.is_underline ? 'underline' : 'none',
          fontStyle: cssObj.is_incline ? 'italic' : 'normal',
          opacity: cssObj.opacity / 100
        };
        if (!cssObj.stroke_color) {
          transCssObj.textShadow = 'none';
        } else {
          const w = `${cssObj.stroke_width}px`;
          const c = `${cssObj.stroke_color}`;
          transCssObj.textShadow = `-${w} -${w} 0 ${c}, ${w} -${w} 0 ${c}, -${w} ${w} 0 ${c}, ${w} ${w} 0 ${c}`;
        }
        this.aiPlayer.updateVodSubTitleStyle(transCssObj);
      },
      destroyPlayer() {
        this.aiPlayer?.destroy();
        const vNode = document.querySelector('#aiVideoPlayer');
        if (vNode) {
          // 清空内容
          vNode.textContent = ''; // vNode.replaceChildren();
        }
      }
    },
    mounted() {
      if (this.$store.state.video.recordInfo) {
        this.initPlayBackInfo();
      }

      // 监听精彩时刻开始截图
      this.$emitter.on(this.$EMIT_DEFINE.addMomentsCutImage, item => {
        this.aiPlayer?.setCurrentTime(Math.floor(item.start_time / 1000));
        this.aiPlayer?.pause();
        setTimeout(() => {
          const params = {
            type: 'image',
            resfile: base64ToFile(
              this.aiPlayer?.videoScreenshot(),
              `moment-${new Date().getTime()}.png`
            ),
            path: `webinars/intro-imgs/${dayjs().format('YYYYMM')}`
          };
          this.$fetch('uploadImage', params, {
            'Content-Type': 'multipart/form-data'
          })
            .then(res => {
              item.image_url = res.data.domain_url;
              this.aiPlayer?.play();
              this.$emitter.emit(this.$EMIT_DEFINE.endMomentsCutImage, {
                ...item,
                uploadStatus: true
              });
            })
            .catch(err => {
              this.aiPlayer?.play();
              this.$emitter.emit(this.$EMIT_DEFINE.endMomentsCutImage, {
                ...item,
                uploadStatus: false
              });
            });
        }, 1000);
      });

      this.$emitter.on(this.$EMIT_DEFINE.momentCurrentTimeClick, start_time => {
        this.aiPlayer?.pause();
        this.aiPlayer?.setCurrentTime(Math.floor(start_time / 1000));
        this.aiPlayer?.play();
      });
    },
    beforeDestroy() {
      this.$emitter.off(this.$EMIT_DEFINE.videoAsrTextUpdate, this.asrTextUpdate);
      this.$emitter.off(this.$EMIT_DEFINE.videoSubtitlesUpdate, this.subtitlesUpdate);
      this.$emitter.off(this.$EMIT_DEFINE.videoSubtitlesCssUpdate, this.subtitlesCssUpdate);
      this.destroyPlayer();
    }
  };
</script>
<style lang="less" scoped>
  .aivideo-player {
    box-sizing: border-box;
    background-color: #000;
    overflow: hidden;

    .vhallPlayer-container {
      display: none;
    }
    .aivideo-player-box {
      height: 189px;
      position: relative;

      #aiVideoPlayer {
        height: 100%;
      }

      .player-pause {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        z-index: 1000;
        display: flex;
        align-items: center;
        justify-content: center;

        p {
          width: 60px;
          height: 60px;
          -webkit-border-radius: 50%;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;

          i {
            font-size: 26px;
            color: #f5f5f5;

            &:hover {
              cursor: pointer;
            }
          }
        }
      }

      .player-controls {
        height: 38px;
        box-sizing: border-box;
        background-color: rgba(26, 26, 26, 0.7);
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 10;
        transform: translateY(100%); /* 开始时, 控制栏在容器下方 */
        opacity: 0; /* 初始透明度为0 */
        transition: transform 0.5s ease-in-out, opacity 0.5s ease-in-out;

        &__progressbar {
          position: absolute;
          top: 0;
          height: 14px;
          width: 100%;
          z-index: 10;

          /deep/ .vh-slider__runway {
            background-color: hsla(0, 0%, 100%, 0.3);
          }

          /deep/ .vh-slider__button {
            width: 9px;
            height: 9px;
            background-color: rgb(251, 38, 38);
          }

          /deep/.vh-slider__button-wrapper {
            &:hover {
              .vh-slider__button {
                width: 12px;
                height: 12px;
                box-shadow: 0 0 10px rgb(251, 38, 38);
              }
            }
          }
        }
        &-progress {
          position: absolute;
          top: -20px;
          left: 0;
          right: 0;
          height: 2px;

          .el-slider__bar {
            height: 4px;
          }
        }

        &__markpoints {
          position: absolute;
          width: 100%;

          .player-markpoint {
            position: absolute;
            width: 6px;
            height: 6px;
            border-radius: 50%;
            top: -5px;
            left: 50%;
            transform: translateX(-50%);
            background-color: #f5f5f5;
            z-index: 12;
            cursor: pointer;
          }
        }

        &__op {
          .vh-iconfont {
            color: #f5f5f5;
          }
        }

        // 播放暂停按钮
        &-play {
          position: absolute;
          left: 15px;
          top: 10px;
          &:hover {
            cursor: pointer;
          }
        }

        // 显示时间
        &-time {
          position: absolute;
          left: 36px;
          top: 12px;
          font-size: 12px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;

          &__current {
            color: #f5f5f5;
          }
          &__divider {
            font-size: 10px;
            padding-right: 3px;
            // color: #888;
            color: #f5f5f5;
          }
          &__total {
            // color: #888;
            color: #f5f5f5;
          }
        }

        // 倍速
        &-speed {
          position: absolute;
          right: 106px;
          top: 8px;
          font-size: 13px;
          color: #e6e6e6;
          background-color: rgb(0, 0, 0, 0.4);
          padding: 6px 10px;
          border-radius: 20px;
          &:hover {
            cursor: pointer;
          }

          &__options {
            position: absolute;
            width: 70px;
            bottom: 30px;
            left: 0;
            background-color: #262626;
            z-index: 30;

            li {
              height: 28px;
              line-height: 28px;
              padding-left: 18px;

              &.selected {
                color: #fb2b2b;
                font-weight: bold;
              }
              &:hover {
                cursor: pointer;
                color: #fb2b2b;
                font-weight: bold;
              }
            }
          }
        }

        // 音量
        &-volume {
          position: absolute;
          right: 76px;
          top: 0;
          padding-top: 12px; // 留白空间，和slider重叠保证鼠标离开喇叭小图标slider不消失
          font-size: 12px;
          z-index: 30;

          &__slider {
            position: absolute;
            top: -96px;
            left: -8px;
            display: none;
          }

          &:hover {
            cursor: pointer;
            .player-controls-volume__slider {
              display: block;
            }
          }
        }

        // 画中画
        &-pip {
          position: absolute;
          right: 46px;
          top: 12px;
          &:hover {
            cursor: pointer;
          }
          i {
            font-size: 17px;
          }
        }

        // 全屏
        &-fullscreen {
          position: absolute;
          right: 15px;
          top: 12px;
          font-size: 12px;
          &:hover {
            cursor: pointer;
          }
        }
      }

      &:hover .player-controls {
        transform: translateY(0%); /* 当鼠标滑入时, 控制栏滑入到位 */
        opacity: 1; /* 同时透明度变成1 */
      }
    }

    .aivideo-player-box.is-fullscreen {
      height: 100%;

      .player-pause {
        p {
          width: 120px;
          height: 120px;

          i {
            font-size: 46px;
          }
        }
      }
    }
  }
</style>
<style lang="less">
  .player-markpoint-popper {
    max-width: 240px;
    max-height: 120px;
    overflow-y: auto;

    .popper__arrow {
      display: none;
    }
  }
</style>
